import { Plus, Moon, Sun, Menu } from 'lucide-react';
import { useAppStore } from '@/stores/appStore';
import { useTheme } from '@/lib/theme';
import { Button } from '@/components/ui/Button';
import { NotificationDropdown } from './NotificationDropdown';

function ThemeToggle() {
  const { resolvedTheme, toggleTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  return (
    <button
      type="button"
      onClick={toggleTheme}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground ring-1 ring-inset ring-hairline transition-[background-color,color,transform] duration-fast ease-sfx hover:bg-tertiary/70 hover:text-foreground active:scale-[0.94]"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

interface TopNavProps {
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  /** Optional secondary (subtle) action rendered before the primary one. */
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

export function TopNav({ title, description, action, secondaryAction }: TopNavProps) {
  const { currentWorkspace, setMobileNavOpen } = useAppStore();

  return (
    <header className="sticky top-0 z-20 shrink-0 border-b border-hairline bg-canvas/75 backdrop-blur-md">
      <div className="flex min-h-16 items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={() => setMobileNavOpen(true)}
          aria-label="Open navigation"
          className="-ml-1 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors duration-fast hover:bg-tertiary/70 hover:text-foreground md:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2.5">
            <h1 className="truncate font-display text-[22px] font-semibold leading-tight tracking-[-0.025em] text-foreground">
              {title}
            </h1>
            {currentWorkspace && (
              <span className="hidden truncate rounded-full bg-tertiary/70 px-2.5 py-0.5 text-[11px] font-medium text-muted-foreground ring-1 ring-inset ring-hairline sm:inline-block">
                {currentWorkspace.name}
              </span>
            )}
          </div>
          {description && (
            <p className="mt-0.5 truncate text-xs text-muted-foreground">{description}</p>
          )}
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {secondaryAction && (
            <Button variant="secondary" size="md" onClick={secondaryAction.onClick}>
              {secondaryAction.label}
            </Button>
          )}
          {action && (
            <Button variant="primary" size="md" onClick={action.onClick} aria-label={action.label}>
              <Plus className="h-4 w-4" strokeWidth={2.5} />
              <span className="hidden sm:inline">{action.label}</span>
            </Button>
          )}
          <span aria-hidden className="mx-1 hidden h-5 w-px bg-hairline sm:block" />
          <ThemeToggle />
          <NotificationDropdown />
        </div>
      </div>
    </header>
  );
}
